import { assertIsDefined } from '@sgme/fp';
import { useCallback, useMemo, useState } from 'react';

import {
  type AccumulatorType,
  type Cleaners,
  type CsvValidationFailure,
  processFileWith,
  type RowPredicate,
} from '@/App/tiles/shared/import/common';
import { ImportConfirmationModal } from '@/App/tiles/shared/import/ImportConfirmationModal';
import { ImportErrorModal } from '@/App/tiles/shared/import/ImportErrorModal';
import { ImportModal } from '@/App/tiles/shared/import/ImportModal';
import { getTradeTypePrefix, type TradeTypeExample } from '@/App/tiles/shared/import/typings.ts';
import { logger } from '@/components/SgwtBootstrap/logger.ts';
import { useAppSelector } from '@/store/hooks.ts';
import { getUserPreferences } from '@/store/userPreferences/slice.ts';

export interface ImportProps<ROW, ERR extends string> {
  requiredColumns: string[];
  mapRowWith: (cleaners: Cleaners) => (parsedRow: readonly unknown[]) => ROW;
  validators: Record<ERR, RowPredicate>;
  isOpen: boolean;
  isEmpty: boolean;
  onImport(rows: readonly ROW[]): void;
  close(): void;
  tradeType: TradeTypeExample;
  accuType?: AccumulatorType;
}

export function Import<ROW, ERR extends string>({
  requiredColumns,
  validators,
  isOpen,
  isEmpty,
  mapRowWith,
  onImport,
  close,
  tradeType,
  accuType,
}: ImportProps<ROW, ERR>) {
  const dateInputCultureInfo = useAppSelector(getUserPreferences).dateInputCultureInfo;
  const [file, setFile] = useState<File | undefined>(undefined);
  const [importResult, setImportResult] = useState<readonly ROW[] | null>(null);
  const [failure, setFailure] = useState<CsvValidationFailure<ERR> | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const processFile = useMemo(
    () => processFileWith<ROW, ERR>(requiredColumns, validators, mapRowWith),
    [mapRowWith, requiredColumns, validators],
  );

  const onFileImport = useCallback(
    async (file: File) => {
      try {
        const result = await processFile(file, dateInputCultureInfo);

        if ('error' in result) {
          setFailure(result);
          setFile(undefined);
          return 'FAILURE';
        }

        setFailure(null);

        if (isEmpty) {
          onImport(result.rows);
          close();
        } else {
          setImportResult(result.rows);
          setShowConfirmation(true);
        }

        return 'SUCCESS';
      } catch (error) {
        logger.logError(
          'Unexpected error while importing file for {tradeType_s}: {errorMessage_s} {errorStack_s}',
          tradeType,
          error instanceof Error ? error.message : String(error),
          error instanceof Error ? error.stack : undefined,
        );
        throw error;
      }
    },
    [close, dateInputCultureInfo, isEmpty, onImport, processFile, tradeType],
  );

  const closeError = useCallback(() => {
    setFailure(null);
  }, []);

  const onCancelConfirmation = useCallback(() => setShowConfirmation(false), []);

  const onConfirm = useCallback(() => {
    try {
      assertIsDefined(importResult, 'onConfirm should only be reachable once importResult has been set');

      onImport(importResult);
      setShowConfirmation(false);
      close();
    } catch (error) {
      logger.logError(
        'Unexpected error while confirming import for {tradeType_s}: {errorMessage_s} {errorStack_s}',
        tradeType,
        error instanceof Error ? error.message : String(error),
        error instanceof Error ? error.stack : undefined,
      );
      throw error;
    }
  }, [onImport, importResult, close, tradeType]);

  return (
    <>
      <ImportModal
        show={isOpen}
        onClose={close}
        onImport={onFileImport}
        tradeType={tradeType}
        accuType={accuType}
        file={file}
        setFile={setFile}
      />

      <ImportErrorModal requiredColumns={requiredColumns} failure={failure} close={closeError} />

      <ImportConfirmationModal
        isOpen={showConfirmation}
        onCancel={onCancelConfirmation}
        onConfirm={onConfirm}
        tradeTypePrefix={getTradeTypePrefix(tradeType)}
      />
    </>
  );
}
